import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import type { GroupedPoll, ManyOfManyPollResponse, Persisted, Poll } from '@tunasong/schemas'
import { Button, ConfirmDialog, FormControlLabel, Radio, RadioGroup, Checkbox, Typography } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { Fragment, useState } from 'react'
import {
  getDefaultGroupWinners,
  getGroupOptionIndices,
  getGroupProgress,
  getEffectiveQuota,
  getGroupVoteCounts,
  getGroupVoters,
  getVotableGroups,
  pickInGroup,
  rankGroupOptions,
} from '../groups.js'
import { PollAudioProvider, WithOptionAudio } from '../option-audio.js'
import { PollOptionName } from '../poll-option-name.js'
import styles from '../poll.module.css'
import Progress from '../progress.js'
import { GroupSection } from './group-section.js'
import css from './grouped.module.css'
import { GroupedPollResult } from './result.js'

const votes = (count: number) => (count === 1 ? '1 vote' : `${count} votes`)

const pickCount = (count: number) => (count === 1 ? 'one' : String(count))

export interface GroupedPollFinalProps {
  poll: Persisted<GroupedPoll>
  onChange(poll: Persisted<Poll>): void
  /** Close the poll with these winners. */
  onConfirm(poll: Persisted<Poll>): void
}

/**
 * The owner picks one winner per group (or the group's quota). The top-voted options start selected, so the
 * common case is one click; ties are said out loud, because the preselection there is arbitrary. Nothing is
 * written until the owner confirms.
 */
export const GroupedPollFinal: FC<GroupedPollFinalProps> = props => {
  const { poll, onChange, onConfirm } = props
  const [pendingEmpty, setPendingEmpty] = useState<string[] | null>(null)

  const { entities: responses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  const { name = 'Untitled', closed } = poll

  if (!hasLoadedChildren) {
    return <div>Loading...</div>
  }

  if (closed) {
    return (
      <PollAudioProvider pollId={poll.id}>
        <div className={styles.container}>
          <Typography className={styles.title} variant="subtitle1">
            <PollIcon /> {name}
          </Typography>
          <GroupedPollResult poll={poll} responses={responses} />
        </div>
      </PollAudioProvider>
    )
  }

  const defaults = getDefaultGroupWinners(poll, responses)
  const hasPicks = Object.values(poll.finalOption ?? {}).some(Boolean)
  /** Shown, not stored: the defaults become the poll's only when the owner changes or confirms them. */
  const finalOption = hasPicks ? (poll.finalOption ?? {}) : defaults.finalOption
  const groups = getVotableGroups(poll)
  const emptyGroups = groups.filter(group => !getGroupOptionIndices(poll, group.id).some(idx => finalOption[idx]))

  /** The owner has not touched this group: what it shows is still the arbitrary default, ties and all. */
  const isDefaultPick = (groupId: string) =>
    getGroupOptionIndices(poll, groupId).every(idx => Boolean(finalOption[idx]) === Boolean(defaults.finalOption[idx]))

  const handlePick = (idx: number) => (value: boolean) =>
    onChange({ ...poll, finalOption: pickInGroup(poll, finalOption, idx, value) })

  const confirm = () => onConfirm({ ...poll, finalOption })

  const handleConfirm = () => {
    if (emptyGroups.length > 0) {
      setPendingEmpty(emptyGroups.map(group => group.name))
      return
    }
    confirm()
  }

  const handleConfirmEmpty = () => {
    setPendingEmpty(null)
    confirm()
  }

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography className={styles.title} variant="subtitle1">
          <PollIcon /> {name}
        </Typography>
        {groups.map(group => {
          const quota = getEffectiveQuota(poll, group)
          const ranked = rankGroupOptions(poll, responses, group.id)
          const counts = getGroupVoteCounts(poll, responses, group.id)
          const voters = getGroupVoters(poll, responses, group.id).length
          const tied = isDefaultPick(group.id) ? defaults.ties.get(group.id) : undefined
          /** The places the tie decides: the tied options the default happened to pick. */
          const tiedPlaces = (tied ?? []).filter(idx => defaults.finalOption[idx]).length
          const rows = ranked.map(idx => {
            const option = poll.pollOptions[idx]
            const checked = Boolean(finalOption[idx])
            const control =
              quota === 1 ? (
                <Radio checked={checked} onChange={() => handlePick(idx)(true)} />
              ) : (
                <Checkbox checked={checked} onChange={ev => handlePick(idx)(ev.target.checked)} />
              )
            return (
              <div key={idx} data-reorder-key={idx}>
                <WithOptionAudio option={option}>
                  <FormControlLabel
                    className={styles.formLabel}
                    control={control}
                    label={
                      <span className={styles.optionBox}>
                        <PollOptionName option={option} withAudio={false} />
                        <Typography variant="caption" color="textSecondary">
                          {`${counts.get(idx) ?? 0} of ${voters}`}
                        </Typography>
                        <Progress value={getGroupProgress(poll, responses, idx)} />
                      </span>
                    }
                  />
                </WithOptionAudio>
              </div>
            )
          })
          const options: ReactNode =
            quota === 1 ? (
              <RadioGroup aria-label={group.name} name={`final-${group.id}`}>
                {rows}
              </RadioGroup>
            ) : (
              rows
            )
          return (
            <GroupSection key={group.id} name={group.name} order={ranked}>
              {voters === 0 ? (
                <Typography variant="caption" color="textSecondary" className={css.notice}>
                  No votes yet, so pick one or leave it empty.
                </Typography>
              ) : null}
              {tied ? (
                <Typography variant="caption" color="secondary" className={css.notice}>
                  Tied:{' '}
                  {tied.map((idx, position) => (
                    <Fragment key={idx}>
                      {position > 0 ? ', ' : null}
                      <PollOptionName option={poll.pollOptions[idx]} withAudio={false} />
                    </Fragment>
                  ))}{' '}
                  have {votes(counts.get(tied[0]) ?? 0)} each, so pick {pickCount(tiedPlaces)}.
                </Typography>
              ) : null}
              {options}
            </GroupSection>
          )
        })}
        <Button
          variant="outlined"
          color="secondary"
          disabled={groups.length === emptyGroups.length}
          onClick={handleConfirm}
        >
          Select final option(s)
        </Button>
        <ConfirmDialog
          open={pendingEmpty !== null}
          title="Some groups have no winner"
          text={`No winner for ${(pendingEmpty ?? []).join(', ')}. Close anyway?`}
          okText="Close anyway"
          onCancel={() => setPendingEmpty(null)}
          onConfirm={handleConfirmEmpty}
        />
      </div>
    </PollAudioProvider>
  )
}

export default GroupedPollFinal
